"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

export type Theme = "light" | "dark" | "system"
type ResolvedTheme = Exclude<Theme, "system">

type ThemeContextValue = {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: string) => void
}

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
  disableTransitionOnChange?: boolean
}

const THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)"
const ThemeContext = createContext<ThemeContextValue | null>(null)

function isTheme(value: string | null): value is Theme {
  return value === "light" || value === "dark" || value === "system"
}

function readStoredTheme(storageKey: string) {
  try {
    return window.localStorage.getItem(storageKey)
  } catch {
    return null
  }
}

function writeStoredTheme(storageKey: string, theme: Theme) {
  try {
    window.localStorage.setItem(storageKey, theme)
  } catch {
    // The selected theme still applies for this session when storage is unavailable.
  }
}

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia(THEME_MEDIA_QUERY).matches ? "dark" : "light"
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? getSystemTheme() : theme
}

function applyTheme(theme: Theme, disableTransition: boolean) {
  const root = document.documentElement
  const resolved = resolveTheme(theme)
  let transitionStyle: HTMLStyleElement | undefined

  if (disableTransition) {
    transitionStyle = document.createElement("style")
    transitionStyle.textContent =
      "*,*::before,*::after{transition:none!important}"
    document.head.appendChild(transitionStyle)
  }

  root.classList.remove("light", "dark")
  root.classList.add(resolved)
  root.style.colorScheme = resolved

  if (transitionStyle) {
    window.getComputedStyle(document.body)
    window.setTimeout(() => transitionStyle?.remove(), 1)
  }

  return resolved
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  disableTransitionOnChange = false,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme)
  const [resolvedTheme, setResolvedTheme] =
    useState<ResolvedTheme>("light")

  const setTheme = useCallback(
    (nextTheme: string) => {
      if (!isTheme(nextTheme)) return
      setThemeState(nextTheme)
      writeStoredTheme(storageKey, nextTheme)
      setResolvedTheme(applyTheme(nextTheme, disableTransitionOnChange))
    },
    [disableTransitionOnChange, storageKey],
  )

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const storedTheme = readStoredTheme(storageKey)
      const initialTheme = isTheme(storedTheme) ? storedTheme : defaultTheme
      setThemeState(initialTheme)
      setResolvedTheme(applyTheme(initialTheme, false))
    })
    return () => window.cancelAnimationFrame(frame)
  }, [defaultTheme, storageKey])

  useEffect(() => {
    if (theme !== "system") return
    const media = window.matchMedia(THEME_MEDIA_QUERY)
    const handleChange = () => setResolvedTheme(applyTheme("system", false))
    media.addEventListener("change", handleChange)
    return () => media.removeEventListener("change", handleChange)
  }, [theme])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || !isTheme(event.newValue)) return
      setThemeState(event.newValue)
      setResolvedTheme(applyTheme(event.newValue, false))
    }
    window.addEventListener("storage", handleStorage)
    return () => window.removeEventListener("storage", handleStorage)
  }, [storageKey])

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [resolvedTheme, setTheme, theme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider")
  }
  return context
}
